import { useEffect, useRef } from 'react';
import { Link } from 'wouter';
import { Sparkles, MapPin } from 'lucide-react';
import CarSpin from './CarSpin';

function CountUp({ end, suffix }: { end: number; suffix: string }) {
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        observer.disconnect();
        let start = 0;
        const duration = 1500;
        const startTime = performance.now();

        function tick(now: number) {
          const progress = Math.min((now - startTime) / duration, 1);
          const eased = 1 - Math.pow(1 - progress, 3);
          start = Math.round(eased * end);
          if (el) el.textContent = `${start}${suffix}`;
          if (progress < 1) requestAnimationFrame(tick);
        }
        requestAnimationFrame(tick);
      },
      { threshold: 0.5 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [end, suffix]);

  return <span ref={ref}>0{suffix}</span>;
}

export default function HeroSection() {
  return (
    <section className="bg-surface overflow-hidden relative">
      <div className="max-w-5xl mx-auto px-4 py-16 md:py-24 relative z-10">
        {/* Split layout: text right + CarSpin left (RTL) */}
        <div className="flex flex-col-reverse md:flex-row items-center gap-8 md:gap-12">
          {/* CarSpin (left in RTL = appears on left visually) */}
          <div className="flex-1 flex justify-center">
            <CarSpin />
          </div>

          {/* Text content (right in RTL = appears on right visually) */}
          <div className="flex-1 text-center md:text-right">
            {/* Badge pill */}
            <div className="anim-fade-up inline-flex items-center gap-2 bg-primary-50 text-primary-600 text-sm font-medium px-4 py-1.5 rounded-full mb-6">
              <Sparkles className="w-4 h-4" />
              أول منصة لغسل السيارات في العراق
            </div>

            {/* Heading */}
            <h1 className="anim-fade-up delay-1 text-3xl md:text-4xl lg:text-5xl font-extrabold text-text-brand font-cairo leading-tight mb-4">
              اغسل سيارتك{' '}
              <span className="text-primary-600">بلا حدود</span>
            </h1>

            {/* Subtitle */}
            <p className="anim-fade-up delay-2 text-base md:text-lg text-text-muted max-w-lg leading-relaxed mb-8 mx-auto md:mx-0">
              اشتراك شهري واحد يتيح لك غسيل سيارتك في أي مغسلة شريكة. اترك سيارتك وتتبع الطابور من هاتفك.
            </p>

            {/* CTAs */}
            <div className="anim-fade-up delay-3 flex flex-col sm:flex-row items-center justify-center md:justify-start gap-3">
              <a
                href="#plans"
                className="btn-primary hidden md:flex items-center gap-2"
              >
                <Sparkles className="w-5 h-5" />
                اشترك الآن
              </a>
              <a
                href="#download"
                className="btn-primary flex md:hidden items-center gap-2"
              >
                <Sparkles className="w-5 h-5" />
                حمّل التطبيق
              </a>
              <Link
                href="/shops"
                className="btn-secondary flex items-center gap-2"
              >
                <MapPin className="w-5 h-5" />
                تصفح المغاسل
              </Link>
            </div>

            {/* Trust line */}
            <p className="anim-fade-up delay-4 text-text-faint text-sm mt-6">
              +50 مغسلة شريكة &middot; حتى 6 غسلات شهرياً &middot; إلغاء بأي وقت
            </p>
          </div>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-3 gap-6 max-w-lg mx-auto mt-16 pt-8 border-t border-gray-200 anim-fade-up">
          <div className="text-center">
            <div className="text-2xl md:text-3xl font-extrabold text-text-brand font-cairo">
              <CountUp end={50} suffix="+" />
            </div>
            <p className="text-text-muted text-sm mt-1">مغسلة شريكة</p>
          </div>
          <div className="text-center">
            <div className="text-2xl md:text-3xl font-extrabold text-text-brand font-cairo">
              <CountUp end={6} suffix="" />
            </div>
            <p className="text-text-muted text-sm mt-1">غسلات شهرياً</p>
          </div>
          <div className="text-center">
            <div className="text-2xl md:text-3xl font-extrabold text-text-brand font-cairo">
              <CountUp end={3} suffix="" />
            </div>
            <p className="text-text-muted text-sm mt-1">باقات اشتراك</p>
          </div>
        </div>
      </div>
    </section>
  );
}
